import { useCallback, type PropsWithChildren } from "react";
import { createPortal } from "react-dom";

import { styled } from "@linaria/react";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import { getCssSizeValue } from "../utils/linaria";

import { useDismiss } from "../hooks/useDismiss";
import { OverlayLayer } from "../overlay/constants";
import { useOverlay } from "../overlay/useOverlay";

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  width?: number | string;
  /**
   * Whether clicking outside the drawer should close it
   * @default false
   */
  closeOnClickOutside?: boolean;
}

const StyledDrawerWrapper = styled.div<{ isOpen: boolean }>`
  position: fixed;

  inset: 0;

  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};

  pointer-events: none;
`;

const DrawerWrapperContent = styled.div`
  position: absolute;

  display: flex;
  align-items: stretch;
  justify-content: flex-end;

  inset: 0;

  pointer-events: none;
`;

const DrawerContentWrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  bottom: 8px;
  left: 0;
  display: flex;
  justify-content: flex-end;
  pointer-events: none;
`;

const DrawerWrapperContentBody = withTheme(styled.div<PropsWithTheme<{ $width?: number | string }>>`
  display: flex;
  flex-direction: column;

  width: ${({ $width }) => getCssSizeValue($width, "60%")};
  height: 100%;

  background-color: ${({ theme }) => theme.color.background.base};
  border: 0.5px solid ${({ theme }) => theme.color.border.primary};
  border-radius: 6px;
  pointer-events: auto;
`);

const Drawer = ({
  open,
  onClose,
  width,
  closeOnClickOutside = false,
  children,
}: PropsWithChildren<DrawerProps>) => {
  const { zIndex, isTopmost } = useOverlay(onClose, open, { layer: OverlayLayer.DRAWER });

  const handleContentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const dismissRef = useDismiss<HTMLDivElement>({
    onDismiss: onClose,
    enabled: open && isTopmost,
    enableClickOutside: closeOnClickOutside && isTopmost,
  });

  return createPortal(
    open && (
      <StyledDrawerWrapper isOpen={open} role="dialog" aria-modal="true" style={{ zIndex }}>
        <DrawerWrapperContent>
          <DrawerContentWrapper>
            <DrawerWrapperContentBody ref={dismissRef} $width={width} onClick={handleContentClick}>
              {children}
            </DrawerWrapperContentBody>
          </DrawerContentWrapper>
        </DrawerWrapperContent>
      </StyledDrawerWrapper>
    ),
    document.body,
  );
};

export default Drawer;
